import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { MasterDataCoverage as Coverage, MasterDataFieldCoverage } from '@/lib/api';
import { DatabaseZap, Loader2 } from 'lucide-react';

function formatPercent(share: number) {
  return new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 1 }).format(share);
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

function formatDateTime(value: string | null) {
  if (!value) return 'noch kein Sync';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function ShareBar({ share, muted }: { share: number; muted: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 rounded-full bg-line overflow-hidden">
        <div
          className={`h-full rounded-full ${muted ? 'bg-ink-soft' : 'bg-risk-yellow'}`}
          style={{ width: `${Math.min(100, Math.round(share * 100))}%` }}
        />
      </div>
      <span className="tabular-nums">{formatPercent(share)}</span>
    </div>
  );
}

export default function MasterDataCoverage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMasterDataCoverage()
      .then(setData)
      .catch((err) => console.error('Fehler beim Laden der Stammdaten-Abdeckung:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-ink-muted">Daten konnten nicht geladen werden.</p>;
  }

  const openDrillDown = (field: MasterDataFieldCoverage) => {
    if (field.missing.articleCount === 0) return;
    navigate(`/lagerrisiken?missingField=${encodeURIComponent(field.field)}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <DatabaseZap className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Stammdaten-Abdeckung</h1>
          <p className="text-[12.5px] text-ink-soft mt-1">
            Wie viel der Bestellempfehlung auf echten ERP-Stammdaten beruht statt auf Systemwerten.
            Basis: letzte {data.window.lookbackDays} Tage, Stand {formatDateTime(data.lastSyncAt)}.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-panel bg-risk-green-soft flex items-center justify-center">
            <DatabaseZap className="h-5 w-5 text-risk-green" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Umsatz auf echten Stammdaten</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-green tabular-nums">
              {formatPercent(data.headline.coveredRevenueShare)}
            </p>
            <p className="text-[11px] text-ink-soft mt-0.5">
              von {formatCurrency(data.scope.revenueInWindow)} im Zeitraum
            </p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Artikel mit Lücke</p>
            <p className="text-2xl font-extrabold tracking-tight text-ink tabular-nums">
              {formatPercent(data.headline.affectedArticleShare)}
            </p>
            <p className="text-[11px] text-ink-soft mt-0.5">von {data.scope.articleCount} Artikeln</p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Davon mit Bedarf</p>
            <p className="text-2xl font-extrabold tracking-tight text-ink tabular-nums">
              {formatPercent(data.headline.affectedDemandArticleShare)}
            </p>
            <p className="text-[11px] text-ink-soft mt-0.5">
              von {data.scope.demandArticleCount} Artikeln mit Verbrauch
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-card border border-line bg-surface overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Feld</th>
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Fehlt bei</th>
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Davon mit Bedarf</th>
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Umsatzanteil</th>
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Systemwert</th>
              <th className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-soft">Auswirkung</th>
            </tr>
          </thead>
          <tbody>
            {data.fields.map((field) => (
              <tr
                key={field.field}
                onClick={() => openDrillDown(field)}
                className={`border-b border-line last:border-0 ${
                  field.scored ? 'text-ink' : 'text-ink-soft'
                } ${field.missing.articleCount > 0 ? 'cursor-pointer hover:bg-surface-hover' : ''}`}
              >
                <td className="px-5 py-3">
                  <span className="font-semibold">{field.label}</span>
                  {!field.scored && (
                    <span className="ml-2 rounded-full border border-line px-2 py-0.5 text-[10px] text-ink-soft">
                      nicht gewertet
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 tabular-nums">
                  {field.missing.articleCount} <span className="text-ink-soft">({formatPercent(field.missing.articleShare)})</span>
                </td>
                <td className="px-5 py-3 tabular-nums">
                  {field.missing.demandArticleCount}{' '}
                  <span className="text-ink-soft">({formatPercent(field.missing.demandArticleShare)})</span>
                </td>
                <td className="px-5 py-3">
                  <ShareBar share={field.missing.revenueShare} muted={!field.scored} />
                </td>
                <td className="px-5 py-3">{field.substitution.label}</td>
                <td className="px-5 py-3 text-ink-soft">{field.substitution.consequence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-card border border-line bg-surface px-5 py-4 space-y-2">
        {data.notes.map((note) => (
          <p key={note.code} className="text-[12px] text-ink-soft">
            {note.text}
          </p>
        ))}
      </div>
    </div>
  );
}
